/**
 * Jednotné hlášení chyb.
 *
 * Aplikace měla desítky prázdných `catch {}` – když něco selhalo, nikdo se to
 * nedozvěděl a problémy se dohledávaly poslepu. Tyhle funkce chybu vždy
 * zapíší do konzole s kontextem a navíc do kruhového bufferu
 * `window.__lksErrors` (posledních 50), takže jde na střelnici otevřít konzoli
 * prohlížeče a vypsat `window.__lksErrors` i bez vývojářského nářadí.
 *
 * Nic nezobrazují – o hlášku uživateli se stará volající (showToast).
 */

type Extra = Record<string, unknown>;

interface ErrorRecord {
  at: string;
  level: "error" | "warning";
  where: string;
  message: string;
  extra?: Extra;
}

declare global {
  interface Window {
    __lksErrors?: ErrorRecord[];
  }
}

const MAX_RECORDS = 50;

/** Čitelný popis čehokoli, co může přiletět do catch. */
export function describeError(err: unknown): string {
  if (err instanceof Error) return err.message || err.name;
  if (typeof err === "string") return err;
  if (err && typeof err === "object" && "message" in err) {
    return String((err as { message: unknown }).message);
  }
  try {
    return JSON.stringify(err);
  } catch {
    return String(err);
  }
}

function remember(record: ErrorRecord): void {
  try {
    if (typeof window === "undefined") return;
    const log = (window.__lksErrors ??= []);
    log.push(record);
    if (log.length > MAX_RECORDS) log.splice(0, log.length - MAX_RECORDS);
  } catch {
    // Diagnostika nikdy nesmí shodit aplikaci.
  }
}

/**
 * Chyba, která uživateli něco rozbila nebo mu znemožnila akci.
 * `where` = krátký název místa, např. "JudgeView.saveScore".
 */
export function reportError(where: string, err: unknown, extra?: Extra): void {
  const message = describeError(err);
  console.error(`[LKS] ${where}: ${message}`, err, extra ?? "");
  remember({ at: new Date().toISOString(), level: "error", where, message, extra });
}

/**
 * Očekávané či neškodné selhání (volitelná data, best-effort úklid, přerušené
 * přehrávání zvuku…). Uživateli se nic neukazuje, ale stopa v bufferu zůstane –
 * ať je vidět, když se „neškodná" chyba začne opakovat.
 */
export function reportWarning(where: string, err: unknown, extra?: Extra): void {
  const message = describeError(err);
  console.warn(`[LKS] ${where}: ${message}`, extra ?? "");
  remember({ at: new Date().toISOString(), level: "warning", where, message, extra });
}
